import type { FC, CSSProperties } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import Tag from 'app/components/Tag';
import { formatGameTimestamp } from 'app/utils/date';
import { assertNever } from 'app/utils/types';
import Tooltip from 'app/components/Tooltip';

import style from './style.scss';

interface Props {
  status: MatchStatus;
  liveTimestamp?: number;
  className?: string;
  style?: CSSProperties;
  liveCheckedIn?: boolean;
}

const MatchStatusTag: FC<Props> = ({
  status,
  liveTimestamp,
  className,
  style: css,
  liveCheckedIn
}) => {
  const [t] = useTranslation();

  return (
    <div className={style.matchStatusTagContainer}>
      {liveCheckedIn && (
        <Tooltip
          active={liveCheckedIn}
          content={t('match_status-live_checked_in')}
          className={style.liveCheckedInContainer}
          contentClassName={style.liveCheckedInTooltip}
        >
          <span className={style.liveCheckedIn} />
        </Tooltip>
      )}

      {(() => {
        switch (status) {
          case 'canceled':
            return (
              <Tag color="grey" filled className={className} style={css}>
                {t('match_status-canceled')}
              </Tag>
            );
          case 'not_booked':
            return null;
          case 'set_up':
            return (
              <Tag color="grey" className={className} style={css}>
                {t('match_status-set_up')}
              </Tag>
            );
          case 'pending':
            return (
              <Tag color="grey" className={className} style={css}>
                {t('match_status-pending')}
              </Tag>
            );
          case 'postponed':
            return (
              <Tag color="orange" className={className} style={css}>
                {t('match_status-postponed')}
              </Tag>
            );
          case 'pre_match':
            return (
              <Tag color="blue" filled className={className} style={css}>
                {t('match_status-pre_match')}
              </Tag>
            );
          case 'live':
            return (
              <Tag color="green" filled className={className} style={css}>
                {liveTimestamp === undefined
                  ? t('match_status-live')
                  : formatGameTimestamp(liveTimestamp)}
              </Tag>
            );
          case 'finished':
            return (
              <Tag color="red" filled className={className} style={css}>
                {t('match_status-completed')}
              </Tag>
            );
          case 'settled':
            return (
              <Tag color="grey" filled className={classnames(style.settled, className)} style={css}>
                {t('match_status-settled')}
              </Tag>
            );
          default:
            assertNever(status);
        }
      })()}
    </div>
  );
};

MatchStatusTag.displayName = 'MatchStatusTag';

export default MatchStatusTag;
